import { v1BackofficeAuthOidcLogout } from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { LanguagePicker } from '@repo/ui/components/language-picker'
import { ThemePicker } from '@repo/ui/components/theme-picker'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@repo/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuItem, useSidebar } from '@repo/ui/sidebar'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { LogOut, Settings } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'

/**
 * The sidebar footer. It used to be just the theme and language pickers —
 * pure client state, no identity required, because MS0 had removed the
 * back-office credential stack along with sign-out. MS4 T4.6
 * brings the account part back on top of the real backoffice session.
 */
function OperatorNavUser() {
	const { t } = useTranslation()
	const { state } = useSidebar()
	const isCollapsed = state === 'collapsed'
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const handleLogout = async () => {
		// `oidc-logout` rather than plain `sign-out`: it also ends Keycloak's
		// own `master`-realm session server-to-server. Plain `sign-out` only
		// ends the local session, and `/login` starts a new OIDC round trip
		// immediately on mount — with the IdP session still alive that just
		// signs the operator back in silently through SSO, so the logout
		// button would visibly do nothing.
		await v1BackofficeAuthOidcLogout({})
		// Clear before navigating: the private gate reads the session
		// cache-first, so a stale authenticated entry would bounce the
		// logout back to the console.
		queryClient.clear()
		await navigate({ to: '/login' })
	}

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger
						render={
							<Button
								aria-label={t(
									'common:app_sidebar.menu.preferences'
								)}
								className='w-full justify-start gap-2 group-data-[collapsible=icon]:justify-center'
								variant='ghost'
							>
								<Settings className='size-4' />
								<span className='group-data-[collapsible=icon]:hidden'>
									{t('common:app_sidebar.menu.preferences')}
								</span>
							</Button>
						}
					/>
					<DropdownMenuContent
						align='center'
						className='w-56'
						side={isCollapsed ? 'right' : 'top'}
						sideOffset={8}
					>
						<ThemePicker />
						<LanguagePicker />
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={handleLogout}>
							<LogOut />
							<span>{t('common:app_sidebar.menu.logout')}</span>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	)
}

export { OperatorNavUser }
